"use client";

import { EntityType } from "@/gen/ts/v1/user/user_service_pb";
import { useQuery } from "@tanstack/react-query";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@workspace/ui/components/avatar";
import { Button } from "@workspace/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@workspace/ui/components/dialog";
import { LoadingSpinner } from "@workspace/ui/components/loading-spinner";
import { ScrollArea } from "@workspace/ui/components/scroll-area";
import Link from "next/link";
import { useState } from "react";
import { user } from "../api/queries.client";

interface UserFollowingDialogProps {
  userId: string;
  followingCount: number;
  trigger?: React.ReactNode;
}

export function UserFollowingDialog({
  userId,
  followingCount,
  trigger,
}: UserFollowingDialogProps) {
  const [open, setOpen] = useState(false);

  // Get following list when dialog opens
  const followingQuery = user.listFollowing({ userId });
  const {
    data: followingData,
    isLoading,
    error,
  } = useQuery({
    ...followingQuery,
    enabled: open && Boolean(userId),
  });

  const defaultTrigger = (
    <Button variant="ghost" className="text-sm">
      <span className="font-semibold">{followingCount}</span>
      <span className="ml-1">following</span>
    </Button>
  );

  // All following entities in a unified list (already sorted by backend)
  const allFollowing = followingData?.following || [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
      <DialogContent className="max-w-md" glassOverlay>
        <DialogHeader>
          <DialogTitle>{followingCount} following</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-96">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner className="h-6 w-6" />
            </div>
          ) : error ? (
            <div className="text-muted-foreground py-8 text-center">
              <p>Error loading following list</p>
              <p className="mt-1 text-sm">{error?.message}</p>
            </div>
          ) : (
            <div>
              {/* Unified Following List */}
              {allFollowing.length > 0 ? (
                <div className="space-y-2 p-2">
                  {allFollowing.map((entity) => {
                    const isOrg = entity.entityType === EntityType.ORGANIZATION;
                    const displayName = isOrg
                      ? entity.username
                      : `@${entity.username}`;
                    const profileUrl = isOrg
                      ? `/profile/org/${entity.username}`
                      : `/profile/${entity.username}`;

                    return (
                      <Link
                        key={entity.userId}
                        href={profileUrl}
                        className="group flex cursor-pointer items-center space-x-3 rounded-lg p-3 transition-colors hover:bg-gray-50"
                      >
                        <div className="shrink-0">
                          <Avatar className="size-12">
                            <AvatarImage
                              src={entity.avatarUrl}
                              alt={entity.username}
                            />
                            <AvatarFallback>
                              {entity.username?.[0]?.toUpperCase() ||
                                (isOrg ? "O" : "U")}
                            </AvatarFallback>
                          </Avatar>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium text-gray-900 transition-colors group-hover:text-blue-600">
                            {displayName}
                          </p>
                        </div>
                        <div className="shrink-0">
                          <svg
                            className="h-4 w-4 text-gray-400 transition-colors group-hover:text-blue-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                /* Empty State */
                <div className="text-muted-foreground py-8 text-center">
                  <p>Not following anyone yet</p>
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
